import { useEffect, useState } from 'react';
import {
  Lightbulb,
  Plus,
  DollarSign,
  Home,
  Car,
  GraduationCap,
  Heart,
  Briefcase,
  Loader2,
  ChevronRight,
  Rocket,
  Stethoscope,
  PiggyBank,
  Laptop,
  X,
  Trash2,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';

const deductionCategories = [
  { value: 'mortgage_interest', label: 'Mortgage Interest', icon: Home },
  { value: 'property_tax', label: 'Property Tax', icon: Home },
  { value: 'home_office', label: 'Home Office', icon: Home },
  { value: 'vehicle', label: 'Vehicle/Mileage', icon: Car },
  { value: 'education', label: 'Education', icon: GraduationCap },
  { value: 'student_loan_interest', label: 'Student Loan Interest', icon: GraduationCap },
  { value: 'health', label: 'Health Insurance', icon: Heart },
  { value: 'retirement', label: 'Retirement', icon: Briefcase },
  { value: 'startup_costs', label: 'Startup Costs', icon: Rocket },
  { value: 'charitable', label: 'Charitable', icon: Heart },
  { value: 'medical', label: 'Medical', icon: Stethoscope },
  { value: 'hsa', label: 'HSA', icon: PiggyBank },
  { value: 'other', label: 'Other', icon: DollarSign },
];

interface QuickAddField {
  name: string;
  label: string;
  type: 'number' | 'text' | 'select';
  max?: number;
  options?: string[];
}

interface QuickAddDeduction {
  id: string;
  category: string;
  title: string;
  description: string;
  maxAmount?: number;
  icon: any;
  color: string;
  bgColor: string;
  fields: QuickAddField[];
  calculateAmount: (data: Record<string, any>) => number;
  defaultDescription: (data: Record<string, any>) => string;
}

// Quick-add deduction templates
const quickAddDeductions: QuickAddDeduction[] = [
  {
    id: 'mortgage_interest',
    category: 'mortgage_interest',
    title: 'Mortgage Interest',
    description: 'From Form 1098 Box 1',
    icon: Home,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    fields: [
      { name: 'amount', label: 'Mortgage Interest Paid (Box 1)', type: 'number' },
      { name: 'lender', label: 'Lender Name', type: 'text' },
    ],
    calculateAmount: (data) => data.amount || 0,
    defaultDescription: (data) => `Mortgage interest - ${data.lender || 'Form 1098'}`,
  },
  {
    id: 'property_tax',
    category: 'property_tax',
    title: 'Property Taxes',
    description: 'Real estate taxes paid',
    icon: Home,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    fields: [
      { name: 'amount', label: 'Property Taxes Paid', type: 'number' },
      { name: 'property', label: 'Property Address', type: 'text' },
    ],
    calculateAmount: (data) => data.amount || 0,
    defaultDescription: (data) => `Property taxes - ${data.property || 'Primary residence'}`,
  },
  {
    id: 'home_office',
    category: 'home_office',
    title: 'Home Office',
    description: '$5/sq ft simplified method (max 300 sq ft)',
    maxAmount: 1500,
    icon: Home,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    fields: [
      { name: 'sqft', label: 'Square Footage (max 300)', type: 'number', max: 300 },
    ],
    calculateAmount: (data) => Math.min(data.sqft || 0, 300) * 5,
    defaultDescription: (data) => `Home office deduction - ${data.sqft} sq ft @ $5/sq ft`,
  },
  {
    id: 'startup_costs',
    category: 'startup_costs',
    title: 'Startup Costs',
    description: 'Up to $5,000 first-year deduction',
    maxAmount: 5000,
    icon: Rocket,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    fields: [
      { name: 'amount', label: 'Total Startup Costs', type: 'number' },
      { name: 'businessName', label: 'Business Name', type: 'text' },
    ],
    calculateAmount: (data) => Math.min(data.amount || 0, 5000),
    defaultDescription: (data) => `Startup costs - ${data.businessName || 'New Business'}`,
  },
  {
    id: 'self_employed_health',
    category: 'health',
    title: 'Self-Employed Health Insurance',
    description: '100% of premiums for you & family',
    icon: Stethoscope,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    fields: [
      { name: 'monthlyPremium', label: 'Monthly Premium', type: 'number' },
      { name: 'months', label: 'Months Covered (1-12)', type: 'number', max: 12 },
    ],
    calculateAmount: (data) => (data.monthlyPremium || 0) * Math.min(data.months || 12, 12),
    defaultDescription: () => 'Self-employed health insurance premiums',
  },
  {
    id: 'sep_ira',
    category: 'retirement',
    title: 'SEP-IRA Contribution',
    description: 'Up to 25% of net self-employment income',
    maxAmount: 69000,
    icon: PiggyBank,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    fields: [
      { name: 'amount', label: 'Contribution Amount', type: 'number' },
    ],
    calculateAmount: (data) => Math.min(data.amount || 0, 69000),
    defaultDescription: () => 'SEP-IRA retirement contribution',
  },
  {
    id: 'student_loan',
    category: 'education',
    title: 'Student Loan Interest',
    description: 'Up to $2,500 above-the-line deduction',
    maxAmount: 2500,
    icon: GraduationCap,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    fields: [
      { name: 'amount', label: 'Interest Paid', type: 'number' },
    ],
    calculateAmount: (data) => Math.min(data.amount || 0, 2500),
    defaultDescription: () => 'Student loan interest paid',
  },
  {
    id: 'hsa',
    category: 'hsa',
    title: 'HSA Contribution',
    description: 'Self: $4,150 / Family: $8,300 (2024)',
    maxAmount: 8300,
    icon: Heart,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    fields: [
      { name: 'coverageType', label: 'Coverage Type', type: 'select', options: ['Self ($4,150 max)', 'Family ($8,300 max)'] },
      { name: 'amount', label: 'Contribution Amount', type: 'number' },
    ],
    calculateAmount: (data) => {
      const max = data.coverageType?.includes('Family') ? 8300 : 4150;
      return Math.min(data.amount || 0, max);
    },
    defaultDescription: (data) => `HSA contribution (${data.coverageType?.includes('Family') ? 'Family' : 'Self'} coverage)`,
  },
  {
    id: 'charitable',
    category: 'charitable',
    title: 'Charitable Donations',
    description: 'Cash or property donations to qualified orgs',
    icon: Heart,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    fields: [
      { name: 'amount', label: 'Total Donations', type: 'number' },
      { name: 'organization', label: 'Organization(s)', type: 'text' },
    ],
    calculateAmount: (data) => data.amount || 0,
    defaultDescription: (data) => `Charitable donations - ${data.organization || 'Various'}`,
  },
  {
    id: 'equipment_179',
    category: 'other',
    title: 'Section 179 (Equipment)',
    description: 'Deduct full cost of business equipment',
    maxAmount: 1160000,
    icon: Laptop,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    fields: [
      { name: 'amount', label: 'Equipment Cost', type: 'number' },
      { name: 'equipmentType', label: 'Equipment Type', type: 'text' },
    ],
    calculateAmount: (data) => data.amount || 0,
    defaultDescription: (data) => `Section 179 - ${data.equipmentType || 'Business equipment'}`,
  },
];

export function DeductionsPage() {
  const { selectedYear, deductions, fetchDeductions, isLoading } = useTaxStore();
  const [showModal, setShowModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [selectedQuickAdd, setSelectedQuickAdd] = useState<QuickAddDeduction | null>(null);
  const [quickAddData, setQuickAddData] = useState<Record<string, any>>({});
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [claimsOfRight, setClaimsOfRight] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    category: 'other',
    description: '',
    amount: '',
  });
  const [claimData, setClaimData] = useState({
    yearRepaid: selectedYear,
    yearOriginallyTaxed: selectedYear - 1,
    amount: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadDeductions();
    fetchDeductions(selectedYear);
  }, [selectedYear, fetchDeductions]);

  const loadDeductions = async () => {
    try {
      const response = await api.getDeductions(selectedYear);
      setSuggestions(response.suggestions || []);
      setClaimsOfRight(response.claimsOfRight || []);
    } catch (err) {
      console.error('Error loading deductions:', err);
    }
  };

  const handleAddDeduction = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.addDeduction(selectedYear, {
        ...formData,
        amount: parseFloat(formData.amount),
        year: selectedYear,
      });
      loadDeductions();
      fetchDeductions(selectedYear);
      setShowModal(false);
      setFormData({ category: 'other', description: '', amount: '' });
    } catch (err) {
      console.error('Error adding deduction:', err);
    }
    setSaving(false);
  };

  const handleAddClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.addClaimOfRight(selectedYear, {
        ...claimData,
        amount: parseFloat(claimData.amount),
      });
      loadDeductions();
      setShowClaimModal(false);
      setClaimData({
        yearRepaid: selectedYear,
        yearOriginallyTaxed: selectedYear - 1,
        amount: '',
        description: '',
      });
    } catch (err) {
      console.error('Error adding claim:', err);
    }
    setSaving(false);
  };

  const openQuickAdd = (template: QuickAddDeduction) => {
    setSelectedQuickAdd(template);
    setQuickAddData({});
    setShowQuickAddModal(true);
  };

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuickAdd) return;

    setSaving(true);
    try {
      const amount = selectedQuickAdd.calculateAmount(quickAddData);
      const description = selectedQuickAdd.defaultDescription(quickAddData);

      await api.addDeduction(selectedYear, {
        category: selectedQuickAdd.category,
        description,
        amount,
        year: selectedYear,
      });
      loadDeductions();
      fetchDeductions(selectedYear);
      setShowQuickAddModal(false);
      setSelectedQuickAdd(null);
      setQuickAddData({});
    } catch (err) {
      console.error('Error adding deduction:', err);
    }
    setSaving(false);
  };

  const getCalculatedAmount = () => {
    if (!selectedQuickAdd) return 0;
    return selectedQuickAdd.calculateAmount(quickAddData);
  };

  const handleDeleteDeduction = async (deductionId: string) => {
    if (!confirm('Are you sure you want to delete this deduction?')) return;
    try {
      await api.deleteDeduction(selectedYear, deductionId);
      fetchDeductions(selectedYear);
      loadDeductions();
    } catch (err) {
      console.error('Error deleting deduction:', err);
    }
  };

  const totalDeductions = deductions.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Deductions</h2>
          <p className="text-muted-foreground">Maximize your tax savings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowClaimModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
          >
            Claim of Right
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
          >
            <Plus className="h-4 w-4" />
            Add Deduction
          </button>
        </div>
      </div>

      {/* Total */}
      <div className="bg-gradient-to-r from-green-500/20 to-green-500/5 border border-green-500/20 rounded-xl p-6">
        <p className="text-sm text-muted-foreground mb-1">Total Deductions</p>
        <p className="text-3xl font-bold text-green-500">
          {formatCurrency(totalDeductions)}
        </p>
      </div>

      {/* Quick Add Deductions */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Quick Add Deductions</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Click to quickly add common deductions with guided forms
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickAddDeductions.map((template) => {
            const Icon = template.icon;
            return (
              <button
                key={template.id}
                onClick={() => openQuickAdd(template)}
                className={cn(
                  "flex flex-col items-center p-4 rounded-xl border border-border",
                  "hover:border-primary/50 hover:shadow-md transition-all duration-200",
                  "text-center group"
                )}
              >
                <div className={cn("p-3 rounded-xl mb-2", template.bgColor)}>
                  <Icon className={cn("h-5 w-5", template.color)} />
                </div>
                <span className="font-medium text-sm">{template.title}</span>
                <span className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {template.description}
                </span>
                {template.maxAmount && (
                  <span className="text-xs text-green-500 mt-1 font-medium">
                    Up to {formatCurrency(template.maxAmount)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            <h3 className="text-lg font-semibold">Potential Deductions</h3>
          </div>
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="p-4 bg-muted/30 rounded-lg border border-border"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{suggestion.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {suggestion.description}
                    </p>
                    {suggestion.requirements && (
                      <ul className="mt-2 text-sm text-muted-foreground list-disc list-inside">
                        {suggestion.requirements.slice(0, 2).map((req: string, i: number) => (
                          <li key={i}>{req}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {suggestion.potentialSavings > 0 && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Up to</p>
                      <p className="text-lg font-bold text-green-500">
                        {formatCurrency(suggestion.potentialSavings)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Claims of Right (Wayfair scenario) */}
      {claimsOfRight.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Claims of Right (IRC §1341)</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Repayments of income previously taxed - like your Wayfair repayment situation
          </p>
          <div className="space-y-3">
            {claimsOfRight.map((claim: any) => (
              <div key={claim.id} className="p-4 bg-muted/30 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{claim.description}</p>
                    <p className="text-sm text-muted-foreground">
                      Repaid in {claim.yearRepaid}, originally taxed in {claim.yearOriginallyTaxed}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Tax Benefit</p>
                    <p className="text-lg font-bold text-green-500">
                      {formatCurrency(claim.taxBenefit)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      via {claim.method === 'credit' ? 'Credit' : 'Deduction'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Deductions */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Your Deductions</h3>
        {deductions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No deductions added yet</p>
            <p className="text-sm">Use Quick Add above or add your own</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deductions.map((deduction: any) => {
              const CategoryIcon = deductionCategories.find(c => c.value === deduction.category)?.icon || DollarSign;
              return (
                <div
                  key={deduction.id}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <CategoryIcon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{deduction.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {deductionCategories.find(c => c.value === deduction.category)?.label || deduction.category}
                        {deduction.autoDetected && (
                          <span className="ml-2 text-xs text-primary">(Auto-detected)</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold text-green-500">
                      {formatCurrency(deduction.amount)}
                    </p>
                    <button
                      onClick={() => handleDeleteDeduction(deduction.id)}
                      className="p-2 rounded-lg hover:bg-destructive/10 transition-smooth"
                      title="Delete deduction"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Deduction Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">Add Deduction</h3>
            <form onSubmit={handleAddDeduction} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                >
                  {deductionCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., Home office expenses"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="0.00"
                  step="0.01"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      {showQuickAddModal && selectedQuickAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", selectedQuickAdd.bgColor)}>
                  <selectedQuickAdd.icon className={cn("h-5 w-5", selectedQuickAdd.color)} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedQuickAdd.title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedQuickAdd.description}</p>
                </div>
              </div>
              <button
                onClick={() => setShowQuickAddModal(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleQuickAdd} className="space-y-4">
              {selectedQuickAdd.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-2">{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      value={quickAddData[field.name] || ''}
                      onChange={(e) => setQuickAddData({ ...quickAddData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                      required
                    >
                      <option value="">Select...</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      value={quickAddData[field.name] || ''}
                      onChange={(e) => setQuickAddData({
                        ...quickAddData,
                        [field.name]: field.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value
                      })}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                      max={field.max}
                      step={field.type === 'number' ? '0.01' : undefined}
                      required
                    />
                  )}
                </div>
              ))}

              {/* Calculated amount preview */}
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-muted-foreground">Calculated Deduction</p>
                <p className="text-2xl font-bold text-green-500">
                  {formatCurrency(getCalculatedAmount())}
                </p>
                {selectedQuickAdd.maxAmount && getCalculatedAmount() >= selectedQuickAdd.maxAmount && (
                  <p className="text-xs text-yellow-500 mt-1">
                    Capped at maximum of {formatCurrency(selectedQuickAdd.maxAmount)}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowQuickAddModal(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || getCalculatedAmount() <= 0}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Add Deduction'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Claim of Right Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-2">Claim of Right (IRC §1341)</h3>
            <p className="text-sm text-muted-foreground mb-4">
              For repaying income you previously paid taxes on (like your Wayfair situation)
            </p>
            <form onSubmit={handleAddClaim} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={claimData.description}
                  onChange={(e) => setClaimData({ ...claimData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., Wayfair signing bonus repayment"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Year Repaid</label>
                  <input
                    type="number"
                    value={claimData.yearRepaid}
                    onChange={(e) => setClaimData({ ...claimData, yearRepaid: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Year Originally Taxed</label>
                  <input
                    type="number"
                    value={claimData.yearOriginallyTaxed}
                    onChange={(e) => setClaimData({ ...claimData, yearOriginallyTaxed: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount Repaid</label>
                <input
                  type="number"
                  value={claimData.amount}
                  onChange={(e) => setClaimData({ ...claimData, amount: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="20000"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowClaimModal(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Calculate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
